import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { api } from "@/services/api/client";
import { socketClient } from "@/services/websocket/socketClient";
import type {
  InviteBundleGrants,
  MasterSponsorship,
  OrgRole,
  PendingProjectGrant,
  SponsorshipBundle,
  SponsorshipInvitePayload,
} from "@/types/sponsorship";
import { getSponsorshipErrorMessage } from "@/utils/sponsorship";

const CACHE_DURATION = 5 * 60 * 1000;

interface PendingOrgGrantPayload {
  orgId: string;
  role: OrgRole;
}

interface PendingProjectGrantPayload {
  projectId: string;
  role: PendingProjectGrant["role"];
}

interface PendingHomepagePayload {
  projectId: string;
  pageId?: string | null;
}

interface PendingGrantsPayload {
  orgGrants?: PendingOrgGrantPayload[];
  projectGrants?: PendingProjectGrantPayload[];
  vaultAssignments?: Array<{
    vaultId: string;
    targetType: "user";
  }>;
  homepage?: PendingHomepagePayload;
}

interface SponsorshipInviteRequest {
  masterBillingOrgId: string;
  email: string;
  message?: string;
  pendingGrants?: PendingGrantsPayload;
}

interface SponsorshipState {
  pendingAsSub: SponsorshipBundle[];
  activeAsSub: MasterSponsorship[];
  peopleAsMaster: MasterSponsorship[];
  pendingCount: number;
  isLoading: boolean;
  error: string | null;
  lastFetched: number | null;
  fetchAll: () => Promise<void>;
  invite: (data: SponsorshipInvitePayload) => Promise<void>;
  resendPendingInvite: (bundleId: string) => Promise<void>;
  cancelPendingInvite: (bundleId: string) => Promise<void>;
  acceptBundle: (bundleId: string) => Promise<void>;
  declineBundle: (bundleId: string) => Promise<void>;
  removeSponsorship: (sponsorshipId: string) => Promise<void>;
  leaveSponsorship: (sponsorshipId: string) => Promise<void>;
  reset: () => void;
}

function toPendingGrantsPayload(
  masterBillingOrgId: string,
  grants?: InviteBundleGrants,
): PendingGrantsPayload | undefined {
  if (!grants) {
    return undefined;
  }

  const pendingGrants: PendingGrantsPayload = {};

  if (grants.org) {
    pendingGrants.orgGrants = [
      {
        orgId: masterBillingOrgId,
        role: grants.org.orgRole,
      },
    ];
  }

  if (grants.projects?.length) {
    pendingGrants.projectGrants = grants.projects.map((grant) => ({
      projectId: grant.projectId,
      role: grant.role,
    }));
  }

  if (grants.vaultAssignment) {
    pendingGrants.vaultAssignments = [
      {
        vaultId: grants.vaultAssignment.vaultId,
        targetType: "user",
      },
    ];
  }

  if (grants.homepage) {
    pendingGrants.homepage = {
      projectId: grants.homepage.projectId,
      ...(grants.homepage.pageId
        ? { pageId: grants.homepage.pageId }
        : {}),
    };
  }

  if (
    pendingGrants.orgGrants === undefined &&
    pendingGrants.projectGrants === undefined &&
    pendingGrants.vaultAssignments === undefined &&
    pendingGrants.homepage === undefined
  ) {
    return undefined;
  }

  return pendingGrants;
}

function toInviteRequest(data: SponsorshipInvitePayload): SponsorshipInviteRequest {
  return {
    masterBillingOrgId: data.masterBillingOrgId,
    email: data.email,
    message: data.message,
    pendingGrants: toPendingGrantsPayload(data.masterBillingOrgId, data.grants),
  };
}

export const useSponsorshipStore = create<SponsorshipState>()(
  subscribeWithSelector(
    immer((set, get) => ({
      pendingAsSub: [],
      activeAsSub: [],
      peopleAsMaster: [],
      pendingCount: 0,
      isLoading: false,
      error: null,
      lastFetched: null,

      fetchAll: async () => {
        const { isLoading, lastFetched } = get();
        if (isLoading) return;
        if (lastFetched && Date.now() - lastFetched < CACHE_DURATION) return;
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });
        try {
          const [pending, active, master] = await Promise.all([
            api.get<SponsorshipBundle[]>("/sponsorships/sub/pending"),
            api.get<MasterSponsorship[]>("/sponsorships/sub/active"),
            api.get<MasterSponsorship[]>("/sponsorships/master/list"),
          ]);
          set((state) => {
            state.pendingAsSub = Array.isArray(pending.data) ? pending.data : [];
            state.activeAsSub = Array.isArray(active.data) ? active.data : [];
            state.peopleAsMaster = Array.isArray(master.data) ? master.data : [];
            state.pendingCount = state.pendingAsSub.length;
            state.isLoading = false;
            state.lastFetched = Date.now();
          });
        } catch (error) {
          set((state) => {
            state.error = getSponsorshipErrorMessage(
              error,
              "Failed to load sponsorships",
            );
            state.isLoading = false;
          });
        }
      },

      invite: async (data) => {
        try {
          await api.post("/sponsorships", toInviteRequest(data));
          set((state) => {
            state.error = null;
            state.lastFetched = null;
          });
          await get().fetchAll();
        } catch (error) {
          const message = getSponsorshipErrorMessage(
            error,
            "Unable to send invite",
          );
          set((state) => {
            state.error = message;
          });
          throw new Error(message);
        }
      },

      resendPendingInvite: async (bundleId) => {
        try {
          await api.post(`/sponsorships/bundles/${bundleId}/resend`);
          set((state) => {
            state.error = null;
            state.lastFetched = null;
          });
          await get().fetchAll();
        } catch (error) {
          const message = getSponsorshipErrorMessage(
            error,
            "Unable to resend invite",
          );
          set((state) => {
            state.error = message;
          });
          throw new Error(message);
        }
      },

      cancelPendingInvite: async (bundleId) => {
        try {
          await api.delete(`/sponsorships/bundles/${bundleId}/cancel`);
          set((state) => {
            state.error = null;
            state.lastFetched = null;
          });
          await get().fetchAll();
        } catch (error) {
          const message = getSponsorshipErrorMessage(
            error,
            "Unable to cancel invite",
          );
          set((state) => {
            state.error = message;
          });
          throw new Error(message);
        }
      },

      acceptBundle: async (bundleId) => {
        try {
          await api.post(`/sponsorships/bundles/${bundleId}/accept`);
          set((state) => {
            state.error = null;
            state.pendingAsSub = state.pendingAsSub.filter((bundle) => bundle._id !== bundleId);
            state.pendingCount = state.pendingAsSub.length;
            state.lastFetched = null;
          });
          await get().fetchAll();
        } catch (error) {
          const message = getSponsorshipErrorMessage(
            error,
            "Unable to accept invite",
          );
          set((state) => {
            state.error = message;
          });
          throw new Error(message);
        }
      },

      declineBundle: async (bundleId) => {
        try {
          await api.post(`/sponsorships/bundles/${bundleId}/decline`);
          set((state) => {
            state.error = null;
            state.pendingAsSub = state.pendingAsSub.filter((bundle) => bundle._id !== bundleId);
            state.pendingCount = state.pendingAsSub.length;
            state.lastFetched = null;
          });
        } catch (error) {
          const message = getSponsorshipErrorMessage(
            error,
            "Unable to decline invite",
          );
          set((state) => {
            state.error = message;
          });
          throw new Error(message);
        }
      },

      removeSponsorship: async (sponsorshipId) => {
        try {
          await api.delete(`/sponsorships/sponsorships/${sponsorshipId}`);
          set((state) => {
            state.error = null;
            state.peopleAsMaster = state.peopleAsMaster.filter((item) => item._id !== sponsorshipId);
            state.lastFetched = null;
          });
        } catch (error) {
          const message = getSponsorshipErrorMessage(
            error,
            "Unable to remove sponsorship",
          );
          set((state) => {
            state.error = message;
          });
          throw new Error(message);
        }
      },

      leaveSponsorship: async (sponsorshipId) => {
        try {
          await api.delete(`/sponsorships/sub/sponsorships/${sponsorshipId}/leave`);
          set((state) => {
            state.error = null;
            state.activeAsSub = state.activeAsSub.filter((item) => item._id !== sponsorshipId);
            state.lastFetched = null;
          });
        } catch (error) {
          const message = getSponsorshipErrorMessage(
            error,
            "Unable to end sponsorship",
          );
          set((state) => {
            state.error = message;
          });
          throw new Error(message);
        }
      },

      reset: () => {
        set((state) => {
          state.pendingAsSub = [];
          state.activeAsSub = [];
          state.peopleAsMaster = [];
          state.pendingCount = 0;
          state.isLoading = false;
          state.error = null;
          state.lastFetched = null;
        });
      },
    })),
  ),
);

let wsListenersRegistered = false;
let wsCleanup: (() => void) | undefined;

export function setupSponsorshipWebSocketEvents(): () => void {
  if (wsListenersRegistered) {
    return wsCleanup ?? (() => {});
  }

  if (!socketClient.isConnected()) {
    return () => {};
  }

  const refresh = () => {
    useSponsorshipStore.setState((state) => {
      state.lastFetched = null;
    });
    void useSponsorshipStore.getState().fetchAll();
  };
  socketClient.onCustomEvent("sponsorship:received", refresh);
  socketClient.onCustomEvent("sponsorship:cancelled", refresh);
  socketClient.onCustomEvent("sponsorship:accepted", refresh);
  socketClient.onCustomEvent("sponsorship:removed", refresh);
  wsListenersRegistered = true;

  wsCleanup = () => {
    socketClient.offCustomEvent("sponsorship:received", refresh);
    socketClient.offCustomEvent("sponsorship:cancelled", refresh);
    socketClient.offCustomEvent("sponsorship:accepted", refresh);
    socketClient.offCustomEvent("sponsorship:removed", refresh);
    wsListenersRegistered = false;
    wsCleanup = undefined;
  };

  return wsCleanup;
}

if (typeof window !== "undefined") {
  setTimeout(() => {
    if (socketClient.isConnected()) {
      setupSponsorshipWebSocketEvents();
    }
  }, 1000);

  socketClient.on("connected", () => {
    setupSponsorshipWebSocketEvents();
  });
}
